import { createElement } from "react";
import { blobToBase64 } from "./blobToBase64";

const PDF_TITLE = "ITSM Maturity Model Summary";
const PDF_FILE_NAME = "ITSM Maturity Model Summary.pdf";

export type ItsmPdfData = {
  image: string;
  name: string;
  company: string;
  challenge?: string;
};

export type ItsmPdfAttachment = {
  base64: string;
  title: string;
  pathOnClient: string;
};

export type GenerateItsmPdfAttachment = (
  data: ItsmPdfData,
) => Promise<ItsmPdfAttachment>;

/**
 * Generates an ITSM Maturity Model PDF and returns the base64 encoded version.
 *
 * This function dynamically imports ItsmPdfTemplate to avoid SSR issues with @react-pdf/renderer.
 * Exposed via window.generateItsmPdfAttachment for Builder.io custom code integration.
 *
 * @param data - PDF content data (image, name, company, challenge)
 * @returns Promise<ItsmPdfAttachment> - Contains base64, title, and pathOnClient
 */
export const generateItsmPdfAttachment: GenerateItsmPdfAttachment = async (
  data,
) => {
  // Dynamic import to avoid SSR issues
  // IMPORTANT: Keep as is - dynamic import doesn't support barrel imports
  const { pdf } = await import("@react-pdf/renderer");
  const { default: ItsmPdfTemplate } =
    await import("@/components/ITSMMaturityModel/ItsmPdf/ItsmPdfTemplate");

  const pdfDocument = createElement(ItsmPdfTemplate, {
    dateType: "US",
    title: PDF_TITLE,
    name: data.name,
    company: data.company,
    challenge: data.challenge,
    imageSrc: data.image,
  });

  const blob = await pdf(pdfDocument).toBlob();
  const base64 = await blobToBase64(blob);

  return {
    base64,
    title: PDF_TITLE,
    pathOnClient: PDF_FILE_NAME,
  };
};
